import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';

import { AccountService, AlertService } from '@app/_services';
import { Role } from '@app/_models';
import { TranslateService } from '@ngx-translate/core';
import { CourseService } from '@app/_services/course.service';
import { utcToZonedTime } from 'date-fns-tz';

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  form: FormGroup;
  loading = false;
  submitted = false;
  currentWeek: number = 0;
  currentPage: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private alertService: AlertService,
    private translate: TranslateService,
    private courseService: CourseService
  ) {}

  switchLanguage(language: string) {
    this.translate.use(language);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  onSubmit() {
    this.submitted = true;

    // reset alerts on submit
    this.alertService.clear();

    // stop here if form is invalid
    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    this.loading = true;
    this.accountService
      .login(this.f.email.value, this.f.password.value)
      .pipe(first())
      .subscribe({
        next: (data) => {
          // get return url from query parameters or default to home page

          // TODO: Return url is always having onboarding, so commented the code, Need to fix it

          let returnUrl = this.route.snapshot.queryParams['returnUrl'];
          let defaultRedirection;
          let activeRole: Role;

          if (data.user.get_role_type_label === 'Doctor') {
            activeRole = Role.Admin;
            defaultRedirection = 'dashboard';

            this.router.navigate([defaultRedirection]);
          } else {
            activeRole = Role.User;

            // Get the user progress
            this.courseService.getUserProgress().subscribe({
              next: (progress: any) => {
                if (progress && Object.keys(progress).length > 0) {
                  this.currentWeek = progress.current_week;
                  this.currentPage = progress.current_page;
                  this.courseService.unlockWeekAndContent(
                    progress.current_week,
                    progress.current_page
                  );
                  const timeZone = 'America/Vancouver';
                  const lastUpdateTimeVancouver = utcToZonedTime(
                    progress.last_update_time,
                    timeZone
                  );
                  const dayDifference = this.courseService.getDayDuration(
                    new Date(),
                    lastUpdateTimeVancouver,
                    timeZone
                  );
                  const currentWeek = progress.current_week;
                  this.courseService.checkUserProgress(
                    currentWeek,
                    dayDifference,
                    false
                  );
                  if (dayDifference < 7) {
                    const progress = { currentWeek: currentWeek };
                    this.courseService.updateLastLoginTime(progress).subscribe({
                      next: () => {
                        console.log('Login time has been updated.');
                      },
                      error: (error) => {
                        this.alertService.error(
                          'Failed to save progress:' + error.message
                        );
                      },
                    });
                  }
                } else {
                  console.log('No user progress found');
                }
                this.navigateToCurrentWeek(this.currentWeek, this.currentPage);
              },
              error: (err) => {
                console.error('Error fetching user progress:', err);
              },
            });

            // defaultRedirection = '/';
          }

          this.accountService.Role = activeRole;
          // TODO: Added the below line bcos, the role related info are not getting updated in the app component,
          // so redirecting the user so as to re-render the whole page
          // window.location.href = defaultRedirection
        },
        error: (error) => {
          const errorMessage =
            error || 'Unable to login at the moment. Please try again later.';
          this.alertService.error(errorMessage);
          this.loading = false;
        },
      });
  }

  navigateToCurrentWeek(currentWeek: number, currentPage: number): void {
    const navigationMap: { [week: number]: { [page: number]: string } } = {
      1: {
        1: 'week1/track_your_sleep',
        2: 'week1/prepare_for_optimal_sleep',
        3: 'week1/take_action',
        4: 'week1/self_assessment_and_reflection',
        5: 'week1/recommended_practices',
      },
      2: {
        1: 'week2/weekly_check_in',
        2: 'week2/review_sleep_journal',
        3: 'week2/time_in_bed_restriction_calculator',
        4: 'week2/understanding_sleep',
        5: 'week2/facts_about_sleep',
        6: 'week2/sleep_as_we_age',
        7: 'week2/self_assessment_and_reflection',
        8: 'week2/plan_for_the_week',
      },
      3: {
        1: 'week3/weekly_check_in',
        2: 'week3/review_sleep_journal',
        3: 'week3/what_controls_sleep',
        4: 'week3/self_assessment_and_reflection',
        5: 'week3/plan_for_the_week',
      },
      4: {
        1: 'week4/weekly_check_in',
        2: 'week4/review_sleep_journal',
        3: 'week4/emotions_and_physical_health',
        4: 'week4/self_assessment_and_reflection',
        5: 'week4/plan_for_the_week',
      },
      5: {
        1: 'week5/weekly_check_in',
        2: 'week5/review_sleep_journal',
        3: 'week5/what_perpetuates_insomnia',
        4: 'week5/self_assessment_and_reflection',
        5: 'week5/self_management_and_relapse_prevention',
      },
      6: {
        1: 'week5/complete_course',
        2: 'journal',
      },
    };

    const route = navigationMap[currentWeek]?.[currentPage] || '/';
    this.router.navigate([route]);
  }
}
